import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LoginDto } from '@monorepo/shared';
import { useLogin } from '../services/auth.service';
import { CardContainer } from '../components/ui/CardContainer';
import { FormInput } from '../components/ui/FormInput';
import { PrimaryButton } from '../components/ui/PrimaryButton';
import { EMAIL_REGEX } from '../constants/validation';
import { env } from '../config/env';
import { AuthLayout } from '../components/layout/AuthLayout';

export const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginDto>();

  const loginMutation = useLogin();

  const onSubmit = async (data: LoginDto) => {
    setEmailNotVerified(false);
    setResendSuccess(false);

    try {
      await loginMutation.mutateAsync(data);
      const redirectUrl = searchParams.get('redirect');
      navigate(redirectUrl ? decodeURIComponent(redirectUrl) : '/');
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage = axiosError?.response?.data?.message || '';
      if (errorMessage === 'email_not_verified') {
        setEmailNotVerified(true);
      }
      console.error('Login failed:', error);
    }
  };

  const handleResendVerification = async () => {
    const email = watch('email');
    if (!email) return;

    setResendingEmail(true);
    setResendSuccess(false);

    try {
      const response = await fetch(`${env.VITE_API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
        credentials: 'include',
      });

      if (response.ok) {
        setResendSuccess(true);
      }
    } catch (error) {
      console.error('Failed to resend verification:', error);
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <AuthLayout>
      <CardContainer>
        <h1 className="text-3xl font-display text-primary mb-2 text-center">
          {t('auth.login.title')}
        </h1>
        <p className="text-secondary text-center mb-8">{t('auth.login.subtitle')}</p>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <FormInput
            label={t('auth.login.email.label')}
            type="email"
            placeholder={t('auth.login.email.placeholder')}
            error={errors.email?.message}
            required
            {...register('email', {
              required: t('auth.login.email.error.required'),
              pattern: {
                value: EMAIL_REGEX,
                message: t('auth.login.email.error.invalid'),
              },
            })}
          />

          <FormInput
            label={t('auth.login.password.label')}
            type="password"
            placeholder={t('auth.login.password.placeholder')}
            error={errors.password?.message}
            required
            {...register('password', {
              required: t('auth.login.password.error.required'),
            })}
          />

          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
              {t('auth.login.cta.forgot_password')}
            </Link>
          </div>

          {emailNotVerified ? (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700 mb-2">
                {t('auth.login.error.email_not_verified')}
              </p>
              {resendSuccess ? (
                <p className="text-sm text-green-600">Email de vérification envoyé avec succès !</p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                  className="text-sm text-primary hover:underline font-medium disabled:opacity-50"
                >
                  {resendingEmail ? 'Envoi en cours...' : t('auth.login.error.resend_verification')}
                </button>
              )}
            </div>
          ) : loginMutation.isError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{t('auth.login.error.invalid_credentials')}</p>
            </div>
          ) : null}

          <PrimaryButton type="submit" loading={loginMutation.isPending}>
            {t('auth.login.cta.submit')}
          </PrimaryButton>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-text">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-primary hover:underline font-medium">
              {t('auth.login.cta.go_to_signup')}
            </Link>
          </p>
        </div>
      </CardContainer>
    </AuthLayout>
  );
};
